// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/DtmfNode.js (816 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react, AudioPreviewModel → AudioPreviewModal.
 *
 * Preserves verbatim: handle-id shape `${id}_handle-${idx}` and single-mode
 * `${id}_single_source`, layout constants (HEAD_H/DIV_H/AUDIO_H/ROW_H), key
 * → handle numeric-index mapping via DTMF_KEYS (shared constant), timeout
 * options 3-5 for multi-digit vs 1-20 for single-digit, digitCount === 1
 * "at least one handle" rule, multi-digit variableName uniqueness check
 * against ancestor `availableVariables`, smsData JSON-in-option encoding
 * with data-id attribute, webhookConfig save via a distinct top-level
 * `updateNodeData(id, { webhookConfig })` call (NOT wrapped in updateNode),
 * numSources derivation from selectedHandles.
 *
 * V1 quirk preserved: two AudioPreviewModal instances both gated by
 * `playModalOpen` — the menu preview and the fallback preview share the
 * same open flag.
 */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Button, Form } from './_rbShim';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import {
  Grid3X3,
  Settings,
  Mail,
  Link2,
  Play,
  Trash2,
  Music,
  Hash,
  ArrowRight,
} from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import { triggerHandleConnect, findUpstreamLanguageNode } from '../Helpers/utils';
import { DTMF_KEYS, ACTION_COLORS } from '@/utilities/constant';
import { NodesContext } from '../provider/IvrContext';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import WebhookSelectModal from '../Helpers/WebhookSelectModal';
import { BACKEND_API_BASE_URL } from '@/utilities/constant';
import { getApiCall } from '@/service/httpCall';
import { toast } from 'sonner';

const DtmfNode = ({ data, id }: any) => {
  const { smsWebhookList, webhookList, updateNodeData, onRemove } = useContext(
    NodesContext,
  ) as any;
  const { getNodes, getEdges } = useReactFlow();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [parentVarList, setParentVarList] = useState<any[]>([]);
  const [varListModalOpen, setVarListModalOpen] = useState(false);
  const [smsModalIsOpen, setSmsModalIsOpen] = useState(false);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [audio, setAudio] = useState<any[]>([]);
  const [noInputFileList, setNoInputFileList] = useState<any[]>([]);
  const [wrongInputFileList, setWrongInputFileList] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [tempSmsData, setTempSmsData] = useState<any>({});
  const [smsErrors, setSmsErrors] = useState<any>({});
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [audioPlay, setAudioPlay] = useState<any>();
  const [audioType, setAudioType] = useState<any>();
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });

  const {
    action = {},
    type = '',
    numDigits = 1,
    timeout = '',
    numSources = [],
    rePrompt = '',
    ttsRows = [],
    minValue = '',
    selectedHandles = {},
    smsData = {},
    webhookConfig = null,
  } = data;
  const menuAudioUrl =
    action?.menu?.audio || action?.menu?.languages?.[0]?.url || '';
  const menuAudioName = action?.menu?.name || '';

  const [draftData, setDraftData] = useState<any>({
    action,
    type,
    numDigits,
    timeout,
    rePrompt,
    selectedHandles,
    passToNextNode: data.passToNextNode || false,
    variableName: data.variableName || '',
    availableVariables: data.availableVariables || [],
  });
  const [answerLangCodes, setAnswerLangCodes] = useState<any[]>([]);
  const [hasLangParent, setHasLangParent] = useState(false);
  const [draftLanguages, setDraftLanguages] = useState<any>({
    menu: action?.menu?.language || '',
    noInputFileId: action?.noInputFileId?.language || '',
    wrongInputFileId: action?.wrongInputFileId?.language || '',
  });

  const getAncestorVariables = () => {
    let currentId = id;
    while (true) {
      const incomingEdge = getEdges().find((e: any) => e.target === currentId);
      if (!incomingEdge) break;
      const parentNode = getNodes().find(
        (n: any) => n.id === incomingEdge.source,
      );
      if (!parentNode) break;
      if (
        Array.isArray(parentNode?.data?.availableVariables) &&
        parentNode.data.availableVariables.length > 0
      ) {
        return parentNode.data.availableVariables;
      }
      currentId = parentNode.id;
    }
    return [];
  };

  const openModal = () => {
    const _answerCodes =
      getNodes().find((n: any) => n.type === 'answerNode')?.data?.languages ||
      [];
    const _langNode = findUpstreamLanguageNode(id, getNodes, getEdges);
    const _langCodes =
      _langNode &&
      Array.isArray(_langNode.data?.selectedLanguages) &&
      _langNode.data.selectedLanguages.length > 0
        ? _langNode.data.selectedLanguages
        : null;
    setHasLangParent(!!_langNode);
    setAnswerLangCodes(_langCodes ?? _answerCodes);
    setParentVarList(getAncestorVariables());

    setDraftData({
      action,
      type,
      numDigits,
      timeout,
      rePrompt,
      selectedHandles,
      passToNextNode: data.passToNextNode || false,
      variableName: data.variableName || '',
      availableVariables: data.availableVariables || [],
    });

    setDraftLanguages({
      menu: action?.menu?.language || '',
      noInputFileId: action?.noInputFileId?.language || '',
      wrongInputFileId: action?.wrongInputFileId?.language || '',
    });
    setErrors({});
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setDraftData({
      action,
      type,
      numDigits,
      timeout,
      rePrompt,
      selectedHandles,
      passToNextNode: data.passToNextNode || false,
      variableName: data.variableName || '',
      availableVariables: data.availableVariables || [],
    });

    setDraftLanguages({
      menu: action?.menu?.language || '',
      noInputFileId: action?.noInputFileId?.language || '',
      wrongInputFileId: action?.wrongInputFileId?.language || '',
    });
    setParentVarList([]);
    setErrors({});
    setModalIsOpen(false);
  };

  const openSmsModal = () => {
    setTempSmsData(JSON.parse(JSON.stringify(smsData || {})));
    setSmsErrors({});
    setSmsModalIsOpen(true);
  };
  const closeSmsModal = () => {
    setTempSmsData({});
    setSmsErrors({});
    setSmsModalIsOpen(false);
  };
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const resp: any = await getApiCall(
          `${BACKEND_API_BASE_URL}/v2/prompt?user_id=${data.userId}`,
        );
        if (!Array.isArray(resp)) return;

        const filtered = resp.filter(
          (item: any) =>
            item.prompt_category === 'general' &&
            Array.isArray(item.prompt_url) &&
            item.prompt_url.some((e: any) => e.status === 1),
        );
        setAudio(filtered);
        setNoInputFileList(filtered);
        setWrongInputFileList(filtered);
      } catch (err) {
        console.error(err);
      }
    };

    fetchPrompts();
  }, [data.userId]);

  const toggleSmsEnable = (key: string) => {
    setTempSmsData((prev: any) => {
      const updated = { ...prev };
      updated[key] ? delete updated[key] : (updated[key] = {});
      return updated;
    });
  };

  const validateSmsData = () => {
    const errors: any = {};

    Object.keys(tempSmsData).forEach((key: any) => {
      if (!tempSmsData[key]?.payload) {
        errors[key] = 'Webhook is required';
      }
    });

    setSmsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updateSmsUrl = (key: string, e: any) => {
    const option = e.target.selectedOptions[0];

    setTempSmsData((prev: any) => ({
      ...prev,
      [key]: {
        payload: option.value,
        id: option.dataset.id,
      },
    }));
  };

  const updateNode = (updates: any) =>
    updateNodeData(id, { ...data, ...updates });

  const saveWebhookConfig = (cfg: any) => {
    updateNodeData(id, { webhookConfig: cfg });
    toast.success(cfg ? 'Webhook saved.' : 'Webhook cleared.');
  };

  const saveSmsData = () => {
    if (!validateSmsData()) return;

    updateNode({ smsData: tempSmsData });
    toast.success('SMS Configuration Saved.');
    closeSmsModal();
  };

  const dtmfHandleMeta = (index: number) => ({
    type: 'source',
    position: 'right',
    top: index * 18 + 20,
  });

  const applySelectedHandles = (updated: any) => {
    setDraftData((prev: any) => ({
      ...prev,
      selectedHandles: updated,
      numSources: Object.keys(updated).map((h) => Number(h.split('-')[1])),
    }));
    if (Object.keys(updated).length > 0) {
      setErrors((prev: any) => {
        if (!prev.selectHandles) return prev;
        const { selectHandles: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const toggleHandle = (index: number) => {
    const handleId = `${id}_handle-${index}`;
    const updated = { ...draftData.selectedHandles };

    if (updated[handleId]) delete updated[handleId];
    else updated[handleId] = dtmfHandleMeta(index);

    applySelectedHandles(updated);
  };

  const selectAllHandles = () => {
    const updated: any = {};
    DTMF_KEYS.forEach((_: any, index: number) => {
      updated[`${id}_handle-${index}`] = dtmfHandleMeta(index);
    });
    applySelectedHandles(updated);
  };

  const deselectAllHandles = () => {
    applySelectedHandles({});
  };

  const updateActionAudio = (key: string, list: any[], value: string) => {
    if (value === '') {
      setDraftData((prev: any) => ({
        ...prev,
        action: {
          ...prev.action,
          [key]: null,
        },
      }));
      setDraftLanguages((prev: any) => ({ ...prev, [key]: '' }));
      return;
    }

    const selected = list.find((i: any) => i.id.toString() === value);
    if (!selected) return;

    const approvedLangs = Array.isArray(selected.prompt_url)
      ? selected.prompt_url.filter(
          (e: any) =>
            e.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(e.language)),
        )
      : [];

    if (hasLangParent) {
      setDraftData((prev: any) => ({
        ...prev,
        action: {
          ...prev.action,
          [key]: {
            id: selected.id,
            name: selected.prompt_name,
            duration: selected?.prompt_duration ?? 0,
            languages: approvedLangs
              .filter((e: any) => e.status === 1)
              .map((e: any) => ({
                language: e.language,
                url: e.url,
                duration: e.duration ?? selected.prompt_duration ?? 0,
                status: e.status,
              })),
          },
        },
      }));
      setDraftLanguages((prev: any) => ({ ...prev, [key]: '' }));
      return;
    }

    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftData((prev: any) => ({
      ...prev,
      action: {
        ...prev.action,
        [key]: {
          id: selected.id,
          audio: autoAudio,
          duration: selected?.prompt_duration ?? 0,
          name: selected.prompt_name,
          language: autoLang,
        },
      },
    }));
    setDraftLanguages((prev: any) => ({ ...prev, [key]: autoLang }));
  };

  const updateActionLanguage = (key: string, list: any[], lang: string) => {
    setDraftLanguages((prev: any) => ({ ...prev, [key]: lang }));
    const promptId = draftData.action?.[key]?.id;
    const sel = list.find(
      (i: any) => i.id?.toString() === promptId?.toString(),
    );
    const entry =
      lang && sel && Array.isArray(sel.prompt_url)
        ? sel.prompt_url.find((e: any) => e.language === lang)
        : null;
    setDraftData((prev: any) => ({
      ...prev,
      action: {
        ...prev.action,
        [key]: {
          ...prev.action?.[key],
          audio: entry?.url || '',
          language: lang,
        },
      },
    }));
  };

  const renderLangControl = (key: string, list: any[], errKey: string) => {
    const sel = list.find(
      (a: any) => a.id?.toString() === draftData.action?.[key]?.id?.toString(),
    );
    if (!sel) return null;
    if (hasLangParent) {
      const langs = draftData.action?.[key]?.languages;
      if (!Array.isArray(langs) || langs.length === 0) return null;
      return (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: '#0f766e',
            background: '#f0fdfa',
            border: '1px solid #99f6e4',
            borderRadius: 8,
            padding: '8px 12px',
          }}
        >
          This node is downstream of a Language node — all {langs.length}{' '}
          approved language{langs.length !== 1 ? 's' : ''} (
          {langs.map((l: any) => l.language?.toUpperCase()).join(', ')}) will
          be saved automatically.
        </div>
      );
    }
    const langs = Array.isArray(sel.prompt_url)
      ? sel.prompt_url.filter(
          (e: any) =>
            e.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(e.language)),
        )
      : [];
    if (langs.length === 0) return null;
    return (
      <>
        <Form.Label
          style={{ marginTop: 8, marginBottom: 4, fontWeight: 500 }}
        >
          Select Audio Language <span style={{ color: 'red' }}>*</span>
        </Form.Label>
        <Form.Select
          value={draftLanguages[key]}
          onChange={(e) => updateActionLanguage(key, list, e.target.value)}
        >
          <option value="">Select Language</option>
          {langs.map((e: any) => (
            <option key={e.language} value={e.language}>
              {e.language?.toUpperCase()}
              {e.name ? ` — ${e.name}` : ''}
            </option>
          ))}
        </Form.Select>
        {errors[errKey] && (
          <Form.Text className="text-danger">{errors[errKey]}</Form.Text>
        )}
      </>
    );
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (!draftData.action?.menu?.id) {
      newErrors.audio = 'Audio is required';
    }
    if (!hasLangParent) {
      if (draftData.action?.menu?.id && !draftLanguages.menu)
        newErrors.menuLanguage = 'Please select a language';
      if (
        draftData.action?.noInputFileId?.id &&
        !draftLanguages.noInputFileId
      )
        newErrors.noInputLanguage = 'Please select a language';
      if (
        draftData.action?.wrongInputFileId?.id &&
        !draftLanguages.wrongInputFileId
      )
        newErrors.wrongInputLanguage = 'Please select a language';
    }
    if (draftData.timeout === '' || draftData.timeout === null)
      newErrors.timeout = 'Wait time is required';

    const digitCount = Number(draftData.numDigits);

    if (digitCount == 1) {
      if (Object.keys(draftData.selectedHandles).length < 1) {
        newErrors.selectHandles = `Atleast Select One Handle`;
      }
    }
    if (digitCount > 1 && draftData.passToNextNode) {
      const trimmed = draftData.variableName?.trim();
      if (!trimmed) {
        newErrors.variableName = 'Variable name is required';
      } else if (parentVarList.some((v: any) => v.name === trimmed)) {
        newErrors.variableName = `"${trimmed}" is already used by a parent node. Please enter a unique name.`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    let availableVariables: any[] = [];
    if (draftData.passToNextNode && draftData.variableName?.trim()) {
      availableVariables = [
        ...getAncestorVariables(),
        { name: draftData.variableName.trim(), nodeId: id, enable: false },
      ];
    }

    updateNode({ ...draftData, availableVariables });
    toast.success('Detail Added Successfully.');
    setModalIsOpen(false);
  };

  // Sorted handle entries by key index for stable ordering
  const sortedHandleEntries = useMemo(
    () =>
      Object.entries(draftData.selectedHandles).sort(([a], [b]) =>
        Number(a.split('-')[1]) - Number(b.split('-')[1]),
      ),
    [draftData.selectedHandles],
  );

  // Layout constants (must match actual CSS heights)
  const HEAD_H = 40;
  const DIV_H = 1;
  const AUDIO_H = 30;
  const ROW_H = 26;

  const showSingle = Number(numDigits) !== 1;
  const handleCount = sortedHandleEntries.length;
  const allDtmfKeysSelected = handleCount === DTMF_KEYS.length;
  const noDtmfKeysSelected = handleCount === 0;
  const dtmfBulkBtnStyle = (disabled: boolean) => ({
    fontSize: 10,
    fontWeight: 600,
    background: '#ede9fe',
    color: '#6366f1',
    border: 'none',
    borderRadius: 4,
    padding: '2px 8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    letterSpacing: 0,
    textTransform: 'none' as const,
  });

  const renderedHandles = useMemo(
    () =>
      sortedHandleEntries.map(([handleId], i) => (
        <Handle
          key={handleId}
          type="source"
          position={Position.Right}
          id={handleId}
          style={{
            top: HEAD_H + DIV_H + AUDIO_H + i * ROW_H + ROW_H / 2,
            backgroundColor: '#7c3aed',
            width: 8,
            height: 8,
          }}
        />
      )),
    [sortedHandleEntries],
  );

  const renderSingle = useMemo(
    () => (
      <Handle
        id={`${id}_single_source`}
        type="source"
        position={Position.Right}
        isConnectable={showSingle}
        style={{
          top: HEAD_H + DIV_H + AUDIO_H + handleCount * ROW_H + ROW_H / 2,
          backgroundColor: '#7c3aed',
          width: 8,
          height: 8,
          opacity: showSingle ? 1 : 0,
          pointerEvents: showSingle ? 'auto' : 'none',
        }}
      />
    ),
    [id, showSingle, handleCount],
  );

  const displayAudio =
    answerLangCodes.length === 0
      ? audio
      : audio.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );
  const displayNoInputList =
    answerLangCodes.length === 0
      ? noInputFileList
      : noInputFileList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );
  const displayWrongInputList =
    answerLangCodes.length === 0
      ? wrongInputFileList
      : wrongInputFileList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': '#7c3aed' } as any}>
      <span
        className="ivrn-icon"
        // Fixed near the header (not the default 30% of node height) so it
        // stays clear of the key rows as the node grows — same as Conditional.
        style={{ background: '#7c3aed', color: '#fff', top: 20 }}
        onClick={triggerHandleConnect}
      >
        <Grid3X3 size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={Position.Left}
        id={id}
        style={{ background: '#7c3aed' }}
      />
      {renderedHandles}
      {renderSingle}

      <div className="ivrn-head">
        <span className="ivrn-title">DTMF / Gather</span>
        {handleCount > 0 && (
          <span className="ivrn-badge ivrn-badge--purple">
            {handleCount}key{handleCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="ivrn-divider" />
      <div className="ivrn-body" style={{ padding: 0 }}>
        <div
          style={{
            height: AUDIO_H,
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px 0 96px',
          }}
        >
          {menuAudioName ? (
            <span className="ivrn-val">
              <Music size={10} style={{ marginRight: 4, flexShrink: 0 }} />
              {menuAudioName}
            </span>
          ) : (
            <span className="ivrn-hint">Click ⚙ to configure</span>
          )}
        </div>
        {sortedHandleEntries.map(([handleId]) => {
          const keyIdx = Number(handleId.split('-')[1]);
          const keyLabel =
            DTMF_KEYS[keyIdx] !== undefined ? DTMF_KEYS[keyIdx] : keyIdx;
          return (
            <div
              key={handleId}
              style={{
                height: ROW_H,
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px 0 96px',
                fontSize: 12,
                fontWeight: 600,
                borderTop: '1px solid #f2f4f7',
                color: '#374151',
              }}
            >
              <Hash
                size={9}
                style={{ marginRight: 5, color: '#7c3aed', flexShrink: 0 }}
              />
              Key {keyLabel}
            </div>
          );
        })}
        {showSingle && (
          <div
            style={{
              height: ROW_H,
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px 0 96px',
              fontSize: 12,
              fontWeight: 600,
              borderTop: '1px solid #f2f4f7',
              color: '#374151',
            }}
          >
            <ArrowRight
              size={9}
              style={{ marginRight: 5, color: '#7c3aed', flexShrink: 0 }}
            />
            Default
          </div>
        )}
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="DTMF / Gather"
        subtitle="Play a menu, capture caller input, and route by key pressed."
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Discard
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        {/* ── Basic Settings ── */}
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Basic Settings</div>
          <Form.Group className="mb-3">
            <Form.Label>
              Menu Audio <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              name="audioId"
              value={draftData.action.menu?.id || ''}
              onChange={(e) =>
                updateActionAudio('menu', audio, e.target.value)
              }
            >
              <option value="">Select audio file</option>
              {displayAudio.map((a: any) => (
                <option key={a.id} value={a.id}>
                  {a.prompt_name}
                </option>
              ))}
            </Form.Control>
            {errors.audio && (
              <Form.Text className="text-danger">{errors.audio}</Form.Text>
            )}
            {renderLangControl('menu', audio, 'menuLanguage')}
            {renderListenBtn(
              draftData.action?.menu?.audio ||
                draftData.action?.menu?.languages?.[0]?.url ||
                '',
              draftData.action?.menu?.name,
            )}
          </Form.Group>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}
          >
            <Form.Group>
              <Form.Label>
                Wait time (s) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="timeout"
                value={draftData.timeout}
                onChange={(e) =>
                  setDraftData((prev: any) => ({
                    ...prev,
                    timeout: e.target.value,
                  }))
                }
              >
                <option value="">Select</option>
                {draftData.numDigits > 1
                  ? [3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))
                  : Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
              </Form.Select>
              {errors.timeout && (
                <Form.Text className="text-danger">{errors.timeout}</Form.Text>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label>Re-prompt count</Form.Label>
              <Form.Select
                name="rePrompt"
                value={draftData.rePrompt}
                onChange={(e) =>
                  setDraftData((prev: any) => ({
                    ...prev,
                    rePrompt: e.target.value,
                  }))
                }
              >
                <option value="">Select</option>
                {[0, 1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Digits required</Form.Label>
              <Form.Control
                type="number"
                name="numDigits"
                value={draftData.numDigits}
                onChange={(e) =>
                  setDraftData((prev: any) => ({
                    ...prev,
                    numDigits: e.target.value,
                  }))
                }
                onBlur={() => {
                  let v = Number(draftData.numDigits);
                  if (!v || v < 1) v = 1;
                  setDraftData((prev: any) => ({
                    ...prev,
                    numDigits: v,
                    selectedHandles: v === 1 ? prev.selectedHandles : {},
                  }));
                }}
              />
            </Form.Group>
          </div>
        </div>

        {/* ── Fallback Audio ── */}
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Fallback Audio</div>
          <Form.Group className="mb-3">
            <Form.Label>
              No-input audio{' '}
              <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                (optional)
              </span>
            </Form.Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Select
                style={{ flex: 1 }}
                value={draftData.action?.noInputFileId?.id || ''}
                onChange={(e) =>
                  updateActionAudio(
                    'noInputFileId',
                    noInputFileList,
                    e.target.value,
                  )
                }
              >
                <option value="">Pick (or leave blank)</option>
                {displayNoInputList.map((a: any) => (
                  <option key={a.id} value={a.id}>
                    {a.prompt_name}
                  </option>
                ))}
              </Form.Select>
              {draftData.action?.noInputFileId?.audio && (
                <button
                  type="button"
                  className="ivr-drawer-play-btn"
                  onClick={() => {
                    setAudioPlay(draftData.action.noInputFileId.audio);
                    setPlayModalOpen(true);
                    setAudioType('No Input Audio Preview');
                  }}
                >
                  ▶
                </button>
              )}
            </div>
            {renderLangControl(
              'noInputFileId',
              noInputFileList,
              'noInputLanguage',
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label>
              Wrong-input audio{' '}
              <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                (optional)
              </span>
            </Form.Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Select
                style={{ flex: 1 }}
                value={draftData.action?.wrongInputFileId?.id || ''}
                onChange={(e) =>
                  updateActionAudio(
                    'wrongInputFileId',
                    wrongInputFileList,
                    e.target.value,
                  )
                }
              >
                <option value="">Pick (or leave blank)</option>
                {displayWrongInputList.map((a: any) => (
                  <option key={a.id} value={a.id}>
                    {a.prompt_name}
                  </option>
                ))}
              </Form.Select>
              {draftData.action?.wrongInputFileId?.audio && (
                <button
                  type="button"
                  className="ivr-drawer-play-btn"
                  onClick={() => {
                    setAudioPlay(draftData.action.wrongInputFileId.audio);
                    setPlayModalOpen(true);
                    setAudioType('Wrong Input Audio Preview');
                  }}
                >
                  ▶
                </button>
              )}
            </div>
            {renderLangControl(
              'wrongInputFileId',
              wrongInputFileList,
              'wrongInputLanguage',
            )}
          </Form.Group>
        </div>

        {/* ── DTMF Keys ── */}
        {Number(draftData.numDigits) === 1 && (
          <div className="ivr-drawer-section">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 14,
              }}
            >
              <div
                className="ivr-drawer-section-title"
                style={{ flex: 1, marginBottom: 0 }}
              >
                Accepted DTMF keys
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={selectAllHandles}
                  disabled={allDtmfKeysSelected}
                  style={dtmfBulkBtnStyle(allDtmfKeysSelected)}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={deselectAllHandles}
                  disabled={noDtmfKeysSelected}
                  style={dtmfBulkBtnStyle(noDtmfKeysSelected)}
                >
                  Deselect All
                </button>
              </div>
            </div>
            {errors.selectHandles && (
              <div
                style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}
              >
                {errors.selectHandles}
              </div>
            )}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: 8,
              }}
            >
              {DTMF_KEYS.map((key: any, idx: number) => {
                const active =
                  draftData.selectedHandles[`${id}_handle-${idx}`] !==
                  undefined;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggleHandle(idx)}
                    style={{
                      height: 44,
                      border: active
                        ? '2px solid #6366f1'
                        : '1.5px solid #dde1e9',
                      borderRadius: 10,
                      background: active ? '#eef2ff' : '#fafbfc',
                      color: active ? '#4f46e5' : '#6b7280',
                      fontWeight: active ? 700 : 500,
                      fontSize: 15,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
            <div
              style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 10 }}
            >
              Each accepted key gets its own output connection point on the
              node.
            </div>
          </div>
        )}

        {/* ── Variable passthrough (multi-digit) ── */}
        {Number(draftData.numDigits) > 1 && (
          <div className="ivr-drawer-section">
            <div className="ivr-drawer-section-title">Variable</div>
            <Form.Group>
              <Form.Check
                type="checkbox"
                id="dtmf-make-available"
                label="Make collected digits available for future nodes"
                checked={draftData.passToNextNode || false}
                onChange={(e) =>
                  setDraftData((prev: any) => ({
                    ...prev,
                    passToNextNode: e.target.checked,
                    variableName: e.target.checked ? prev.variableName : '',
                  }))
                }
              />
            </Form.Group>
            {draftData.passToNextNode && (
              <Form.Group style={{ marginTop: 12 }}>
                <Form.Label>Variable name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. {customer_number}"
                  value={draftData.variableName}
                  onChange={(e) =>
                    setDraftData((prev: any) => ({
                      ...prev,
                      variableName: e.target.value,
                    }))
                  }
                />
                {errors.variableName && (
                  <Form.Text className="text-danger">
                    {errors.variableName}
                  </Form.Text>
                )}
                {parentVarList.length > 0 && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: '#6366f1',
                      fontWeight: 600,
                    }}
                  >
                    {parentVarList.map((v: any, i: number) => (
                      <span
                        key={i}
                        style={{
                          display: 'inline-block',
                          background: '#eef2ff',
                          borderRadius: 6,
                          padding: '2px 8px',
                          marginRight: 6,
                          marginTop: 4,
                        }}
                      >
                        {v.name}
                      </span>
                    ))}
                  </div>
                )}
              </Form.Group>
            )}
          </div>
        )}
      </IvrDrawer>

      {/* SMS Drawer */}
      <IvrDrawer
        show={smsModalIsOpen}
        onHide={closeSmsModal}
        title="SMS Configuration"
        subtitle="Enable SMS notifications per DTMF key press."
        footer={
          <>
            <Button variant="secondary" onClick={closeSmsModal}>
              Discard
            </Button>
            <Button variant="primary" onClick={saveSmsData}>
              Save
            </Button>
          </>
        }
      >
        {Object.keys(draftData.selectedHandles).length === 0 ? (
          <p>No DTMF key selected.</p>
        ) : (
          Object.keys(draftData.selectedHandles).map((handleKey: any) => {
            const index = handleKey.split('-')[1];
            const keyLabel = DTMF_KEYS[index];
            const lable = `DTMF_${keyLabel}`;
            const checked = !!tempSmsData[lable];
            return (
              <Form.Group className="mb-3" key={handleKey}>
                <Form.Check
                  type="checkbox"
                  id={`dtmf-${index}`}
                  label={`DTMF ${keyLabel}`}
                  checked={checked}
                  required
                  onChange={() => toggleSmsEnable(lable)}
                />
                {checked &&
                  smsWebhookList &&
                  smsWebhookList.length > 0 && (
                    <>
                      <Form.Select
                        className="mt-2"
                        value={tempSmsData[lable]?.payload || ''}
                        onChange={(e) => updateSmsUrl(lable, e)}
                      >
                        <option value="">Select SMS Webhook</option>
                        {smsWebhookList.map((obj: any, idx: number) => {
                          const valueObj = {
                            url: obj.url,
                            smsText: obj.sms_text,
                            requestType: obj.request_type,
                            payload: obj.payload,
                            header: obj.header,
                          };
                          return (
                            <option
                              data-id={obj.id}
                              value={JSON.stringify(valueObj)}
                              key={idx}
                            >
                              {obj.webhook_name}
                            </option>
                          );
                        })}
                      </Form.Select>
                      {smsErrors[lable] && (
                        <Form.Text className="text-danger">
                          {smsErrors[lable]}
                        </Form.Text>
                      )}
                    </>
                  )}
              </Form.Group>
            );
          })
        )}
      </IvrDrawer>

      <WebhookSelectModal
        show={webhookModalOpen}
        onClose={() => setWebhookModalOpen(false)}
        value={webhookConfig}
        onSave={saveWebhookConfig}
        webhookList={webhookList}
        smsWebhookList={smsWebhookList}
        title="DTMF Node Webhook"
      />

      <AudioPreviewModal
        show={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
        audioUrl={menuAudioUrl}
        title={menuAudioName || 'Menu Audio Preview'}
      />

      <AudioPreviewModal
        show={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
        audioUrl={audioPlay}
        title={audioType}
      />

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        {Boolean(handleCount) && (
          <button
            className="ivrn-btn ivrn-btn--sms"
            title="SMS Settings"
            onClick={openSmsModal}
          >
            <Mail size={14} strokeWidth={2} />
          </button>
        )}
        <button
          className={`ivrn-btn ivrn-btn--link${webhookConfig ? ' active' : ''}`}
          title="Webhook"
          onClick={() => setWebhookModalOpen(true)}
        >
          <Link2 size={14} strokeWidth={2} />
        </button>
        {menuAudioUrl && (
          <button
            className="ivrn-btn ivrn-btn--play"
            title="Preview"
            onClick={() => setPlayModalOpen(true)}
          >
            <Play size={14} strokeWidth={2} />
          </button>
        )}
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={() => onRemove(id)}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>

      {/* Already Used Variables Drawer */}
      <IvrDrawer
        show={varListModalOpen}
        onHide={() => setVarListModalOpen(false)}
        title="Used Variables"
        subtitle="Variables already defined in parent nodes of this flow."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setVarListModalOpen(false)}
            >
              Close
            </Button>
          </>
        }
      >
        {parentVarList.length === 0 ? (
          <p className="text-muted" style={{ fontSize: 13 }}>
            No variables from parent nodes.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {parentVarList.map((v: any, i: number) => {
              const numericId = getNodes().findIndex(
                (n: any) => n.id === v.nodeId,
              );
              return (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div
                    style={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: '#3b5bdb',
                        fontSize: 14,
                      }}
                    >
                      {v.name}
                    </span>
                    <span style={{ fontSize: 11, color: '#888' }}>
                      Node ID: {numericId}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </IvrDrawer>
    </div>
  );
};

export default DtmfNode;
